import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Pencil, Trash2, Plus, ArrowUp, ArrowDown, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cls = 'bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white';
const DATE_FORMATS = { '%Y-%m-%d': 'AAAA-MM-JJ', '%d/%m/%Y': 'JJ/MM/AAAA', '%m/%d/%Y': 'MM/JJ/AAAA', '%d-%m-%Y': 'JJ-MM-AAAA', '%Y%m%d': 'AAAAMMJJ' };
const DELIMS = { ',': 'Virgule', ';': 'Point-virgule', '\t': 'Tabulation', '|': 'Barre' };

const ColumnRow = ({ col, i, fields, onChange, onMove, onRemove }) => (
  <div className="flex gap-2 items-center" data-testid={`template-col-${i}`}>
    <select value={col.field || '__const'} onChange={e => onChange(e.target.value === '__const' ? { field: '', value: col.value || '' } : { field: e.target.value, value: null })}
      className={`${cls} w-48`} data-testid={`template-col-field-${i}`}>
      {Object.entries(fields).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      <option value="__const">Valeur fixe…</option>
    </select>
    {!col.field && <input value={col.value || ''} onChange={e => onChange({ value: e.target.value })} placeholder="Valeur" className={`${cls} w-32`} />}
    <input value={col.header} onChange={e => onChange({ header: e.target.value })} placeholder="Titre de colonne" className={`${cls} flex-1`} data-testid={`template-col-header-${i}`} />
    <button onClick={() => onMove(-1)} className="text-slate-500 hover:text-white"><ArrowUp size={14} /></button>
    <button onClick={() => onMove(1)} className="text-slate-500 hover:text-white"><ArrowDown size={14} /></button>
    <button onClick={onRemove} className="text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
  </div>
);

const TemplateEditor = ({ draft, setDraft, meta, onSave, onCancel }) => {
  const fields = draft.mode === 'journal' ? meta.journal_fields : meta.doc_fields;
  const cols = draft.columns;
  const setCols = (c) => setDraft({ ...draft, columns: c });
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= cols.length) return; const c = [...cols]; [c[i], c[j]] = [c[j], c[i]]; setCols(c); };
  return (
    <div className="bg-slate-900 border border-amber-500/40 rounded-xl p-5 space-y-4" data-testid="template-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold">{draft.id ? 'Modifier le modèle' : 'Nouveau modèle'}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-white"><X size={18} /></button>
      </div>
      <div className="grid gap-3 md:grid-cols-5">
        <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Nom du modèle" className={`${cls} md:col-span-2`} data-testid="template-name" />
        <select value={draft.mode} onChange={e => setDraft({ ...draft, mode: e.target.value, columns: [] })} className={cls} data-testid="template-mode">
          <option value="documents">Une ligne par document</option><option value="journal">Écritures débit / crédit</option>
        </select>
        <select value={draft.date_format} onChange={e => setDraft({ ...draft, date_format: e.target.value })} className={cls}>
          {Object.entries(DATE_FORMATS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="flex gap-2">
          <select value={draft.delimiter} onChange={e => setDraft({ ...draft, delimiter: e.target.value })} className={`${cls} flex-1`} title="Séparateur CSV">
            {Object.entries(DELIMS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={draft.decimal} onChange={e => setDraft({ ...draft, decimal: e.target.value })} className={cls} title="Décimale">
            <option value=".">1.50</option><option value=",">1,50</option>
          </select>
        </div>
      </div>
      <div className="space-y-2">
        {cols.map((c, i) => (
          <ColumnRow key={i} col={c} i={i} fields={fields} onMove={d => move(i, d)} onRemove={() => setCols(cols.filter((_, j) => j !== i))}
            onChange={p => setCols(cols.map((x, j) => j === i ? { ...x, ...p } : x))} />
        ))}
        <button onClick={() => { const [k, l] = Object.entries(fields)[0]; setCols([...cols, { field: k, header: l }]); }}
          className="flex items-center gap-1 text-amber-400 text-sm" data-testid="template-add-col"><Plus size={14} /> Ajouter une colonne</button>
      </div>
      <button onClick={onSave} disabled={!draft.name || !cols.length} className="px-5 py-2 rounded-full bg-amber-500 text-black font-semibold text-sm disabled:opacity-40" data-testid="template-save">Enregistrer le modèle</button>
    </div>
  );
};

export const ExportTemplatesTab = ({ token }) => {
  const [meta, setMeta] = useState(null);
  const [draft, setDraft] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const load = () => axios.get(`${API_URL}/api/accounting/templates`, { headers }).then(r => setMeta(r.data));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const body = { name: draft.name, mode: draft.mode, date_format: draft.date_format, delimiter: draft.delimiter, decimal: draft.decimal,
      columns: draft.columns.map(c => ({ field: c.field || '', header: c.header, value: c.field ? null : (c.value || '') })) };
    try {
      if (draft.id) await axios.put(`${API_URL}/api/accounting/templates/${draft.id}`, body, { headers });
      else await axios.post(`${API_URL}/api/accounting/templates`, body, { headers });
      toast.success('Modèle enregistré'); setDraft(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    await axios.delete(`${API_URL}/api/accounting/templates/${t.id}`, { headers }); load();
  };

  if (!meta) return <p className="text-slate-500 text-sm">Chargement…</p>;
  const blank = { name: '', mode: 'documents', date_format: '%Y-%m-%d', delimiter: ',', decimal: '.', columns: [] };

  return (
    <div className="space-y-4" data-testid="export-templates-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400 max-w-3xl">Chaque modèle décrit une destination. Les modèles fournis sont des points de départ : dupliquez-en un pour l'ajuster au format exact demandé par votre logiciel ou votre comptable.</p>
        <button onClick={() => setDraft(blank)} className="flex items-center gap-1 px-4 py-2 rounded-full border border-slate-700 text-sm text-white hover:border-amber-500" data-testid="template-new"><Plus size={15} /> Nouveau</button>
      </div>
      {draft && <TemplateEditor draft={draft} setDraft={setDraft} meta={meta} onSave={save} onCancel={() => setDraft(null)} />}
      <div className="bg-slate-900 border border-slate-800 rounded-xl divide-y divide-slate-800">
        {meta.templates.map(t => (
          <div key={t.id} className="flex items-center gap-4 px-4 py-3" data-testid={`template-row-${t.id}`}>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">{t.name}</p>
              <p className="text-xs text-slate-500">{t.mode === 'journal' ? 'Écritures débit / crédit' : 'Une ligne par document'} · {t.columns.map(c => c.header).join(', ')}</p>
            </div>
            {t.builtin ? <span className="text-xs text-slate-500">Fourni</span> : (
              <>
                <button onClick={() => setDraft({ ...t })} className="text-slate-400 hover:text-white" data-testid={`template-edit-${t.id}`}><Pencil size={15} /></button>
                <button onClick={() => remove(t)} className="text-slate-400 hover:text-red-400" data-testid={`template-delete-${t.id}`}><Trash2 size={15} /></button>
              </>
            )}
            <button onClick={() => setDraft({ ...t, id: undefined, name: `${t.name} (copie)` })} className="text-slate-400 hover:text-amber-400" title="Dupliquer" data-testid={`template-copy-${t.id}`}><Copy size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
